/**
 * RevokePermissionConfirmModal Component
 *
 * Confirmation dialog for revoking user permissions with reason tracking.
 */

import React, { useState, useEffect } from 'react';
import {
  X,
  AlertTriangle,
  Shield,
  FileText,
  XCircle,
  Info,
} from 'lucide-react';
import { AdminUser } from '../../services/adminApi';

interface RevokePermissionConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
  user: AdminUser | null;
  permissionKey: string;
  permissionCategory?: string;
  permissionDescription?: string;
}

export const RevokePermissionConfirmModal: React.FC<RevokePermissionConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  user,
  permissionKey,
  permissionCategory,
  permissionDescription,
}) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setReason('');
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validate reason
    if (!reason || reason.trim().length < 10) {
      setError('Reason must be at least 10 characters');
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke permission');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setReason(e.target.value);
    if (error) {
      setError(null);
    }
  };

  // Get permission name from key (last part after dot)
  const getPermissionName = () => {
    const parts = permissionKey.split('.');
    return parts[parts.length - 1].replace(/_/g, ' ');
  };

  // Get category name
  const getCategoryDisplay = () => {
    if (!permissionCategory) {
      const parts = permissionKey.split('.');
      return parts[0];
    }
    return permissionCategory;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[var(--z-modal)] overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-lg transform rounded-2xl bg-white shadow-xl transition-all">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <div className="flex items-center gap-2">
              <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">
                Revoke Permission
              </h3>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6">
            {/* Warning Message */}
            <div className="mb-6">
              <p className="text-gray-700 mb-4">
                Are you sure you want to revoke this permission?
              </p>

              {/* Permission Details */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <Shield className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">User</p>
                    <p className="text-sm font-medium text-gray-900">{user?.email}</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <XCircle className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Permission</p>
                    <p className="text-sm font-medium text-gray-900 font-mono break-all">
                      {permissionKey}
                    </p>
                    {permissionDescription && (
                      <p className="text-xs text-gray-600 mt-1">{permissionDescription}</p>
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <Info className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Category</p>
                    <p className="text-sm text-gray-700 capitalize">{getCategoryDisplay()}</p>
                  </div>
                </div>
              </div>

              {/* Impact Warning */}
              <div className="mt-4 flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-red-800 mb-1">Impact</p>
                  <p className="text-sm text-red-700">
                    The user will immediately lose access to{' '}
                    <span className="font-medium">{getPermissionName()}</span> functionality. This
                    action will be logged in the audit trail.
                  </p>
                </div>
              </div>
            </div>

            {/* Reason Input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Revocation <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FileText className="absolute left-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
                <textarea
                  value={reason}
                  onChange={handleReasonChange}
                  placeholder="Why is this permission being revoked?"
                  rows={3}
                  disabled={isSubmitting}
                  className={`
                    w-full pl-10 pr-4 py-2.5 border rounded-lg text-sm resize-none
                    focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${error ? 'border-red-300 bg-red-50' : 'border-gray-300'}
                  `}
                />
              </div>
              <div className="flex items-center justify-between mt-1">
                {error ? (
                  <p className="text-xs text-red-500">{error}</p>
                ) : (
                  <p className="text-xs text-gray-500">Minimum 10 characters</p>
                )}
                <p className="text-xs text-gray-500">{reason.length} characters</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !reason}
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors
                  ${
                    isSubmitting || !reason
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }
                `}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Revoking...
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4" />
                    Revoke Permission
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
